import { t, tn } from '@app/i18n';
import {
  CLAIM_OPERATORS,
  claimTextCondition,
  type ClaimOperator,
} from '@app/main/Policies/PolicyModel';
import { useState } from 'react';
import { Badge, Button, Chip, IconButton, SearchCombobox, TextInput } from '@zarya/ui';
import type { ClaimPair, Lens, LensSelection } from './AccessMap.api';
import { LENS_EMPTY, lensActive } from './AccessMap.api';
import s from './AccessMap.module.css';

/**
 * A lente **«Ver como»** de T19 (Fase 3 da spec Human Access × Identity Access,
 * §9).
 *
 * O QUE ELA FAZ COM O MAPA
 *
 *	Sem lente o mapa mostra o eixo identidade puro. Escolhida uma regra de Human
 *	Access — um perfil, que é um conjunto de claims com nome —, nasce UM nó
 *	humano ligado às identidades que a regra concede, o que a pessoa alcança fica
 *	em realce e o resto esmaece.
 *
 * POR QUE UMA REGRA, E NÃO CLAIMS À MÃO, COMO PADRÃO
 *
 *	«Perfil analista-financeiro» diz mais do que `cargo=analista·setor=financeiro`,
 *	e o perfil é a entidade que o operador administra em Human Access. O par
 *	claim=valor avulso continua disponível como **modo avançado**, para a pergunta
 *	ad-hoc que nenhum perfil responde.
 *
 * A lente vive na URL (`?view_as=`), então o link é compartilhável — «o mapa
 * como auditor» é uma coisa que se manda por mensagem.
 */

// Funções, e não constantes de módulo: uma const é avaliada no import e
// congelaria o idioma da primeira renderização (o seletor trocaria tudo menos a
// lente). Continuam exportadas — os testes e o page-object as citam.
/** `lens.rules[].claims[]` → texto no idioma da tela; operador fora do enum cai no `text` do servidor. */
function claimTextFromLens(c: {
  claim: string;
  operator: string;
  values: string[];
  text: string;
}): string {
  return (CLAIM_OPERATORS as readonly string[]).includes(c.operator)
    ? claimTextCondition({
        claim: c.claim,
        operator: c.operator as ClaimOperator,
        values: c.values,
      })
    : c.text;
}

export const LENS_TITLE = () => t('accessMap.lens.title');
export const NO_LENS = () => t('accessMap.lens.none');
export const OPEN_ADVANCED = () => t('accessMap.lens.openAdvanced');
export const CLOSE_ADVANCED = () => t('accessMap.lens.closeAdvanced');
export const WITHOUT_RULES = () => t('accessMap.lens.withoutRules');
/** Primeira abertura (05/09): a lente aparece antes das regras chegarem. */
export const LOADING_RULES = () => t('accessMap.lens.loadingRules');

/** Valor do combobox que abre o modo avançado. */
const ADVANCED = '__claims__';

export interface LensBarProps {
  /** O estado da lente (o mesmo que está na URL). */
  selection: LensSelection;
  /** O que o servidor respondeu sobre a lente — regras, rótulo, avisos. */
  lens: Lens;
  onChange: (selection: LensSelection) => void;
  /**
   * A lente está recalculando (05/09): a leitura nova ainda não voltou e o
   * mapa na tela é o anterior. Tudo o que muda a lente fica desligado — um
   * clique aqui no meio do cálculo enfileiraria um pedido que atropela o outro.
   */
  busy?: boolean;
}

export function LensBar({ selection, lens, onChange, busy = false }: LensBarProps) {
  // O modo avançado abre por escolha no seletor e fica aberto enquanto o
  // operador edita — inclusive com zero pares, que é como ele começa.
  const [advanced, setAdvanced] = useState(selection.claims.length > 0);
  const [claim, setClaim] = useState('');
  const [value, setValue] = useState('');

  const rules = lens.rules ?? [];
  const options = [
    ...rules.map((rule) => ({
      value: rule.policy_id,
      // A opção é o NOME da regra (`label` = `Policy.name`, dado da pessoa). A dica é a
      // condição composta AQUI a partir de `claims[]` (`{claim, operator, values}`, que o
      // contrato já manda) — no idioma da tela; o `text` do servidor (pt-BR) não é lido.
      label: rule.label,
      hint: rule.claims.map(claimTextFromLens).join(' · '),
    })),
    { value: ADVANCED, label: OPEN_ADVANCED() },
  ];

  function chooseRule(next: string | null) {
    if (next === ADVANCED) {
      setAdvanced(true);
      onChange({ regra: null, claims: selection.claims });
      return;
    }
    setAdvanced(false);
    onChange(next ? { regra: next, claims: [] } : LENS_EMPTY);
  }

  function addClaim() {
    const nome = claim.trim();
    const valor = value.trim();
    if (!nome || !valor) return;
    setClaim('');
    setValue('');
    onChange({ regra: null, claims: [...selection.claims, { claim: nome, value: valor }] });
  }

  function removeClaim(index: number) {
    const next = selection.claims.filter((_, position) => position !== index);
    onChange({ regra: null, claims: next });
  }

  const active = lensActive(selection);

  return (
    <section
      className={s.lens}
      data-testid="lens"
      aria-label={LENS_TITLE()}
      aria-busy={busy || undefined}
    >
      <div className={s.lensRow}>
        <span className={s.lensTitle}>{LENS_TITLE()}</span>

        <span className={s.lensPicker}>
          <SearchCombobox
            id="lens-rule"
            label={LENS_TITLE()}
            size="sm"
            options={options}
            value={advanced ? ADVANCED : selection.regra}
            onChange={chooseRule}
            disabled={busy}
            clearable
            // Sem regras E ocupada é «ainda não chegaram», não «não há»: a moldura
            // renderiza antes da primeira leitura (`LENS_PENDING`).
            placeholder={
              rules.length === 0 ? (busy ? LOADING_RULES() : WITHOUT_RULES()) : NO_LENS()
            }
            searchPlaceholder={t('accessMap.lens.filterRules')}
            emptyMessage={t('accessMap.lens.noRuleMatches')}
          />
        </span>

        {active ? (
          <Button
            variant="out"
            size="sm"
            icon="close"
            data-testid="lens-clear"
            disabled={busy}
            onClick={() => {
              setAdvanced(false);
              onChange(LENS_EMPTY);
            }}
          >
            {NO_LENS()}
          </Button>
        ) : null}

        {/* A contagem de perguntas ao PDP: é o número que responde "por que esta
            montagem demorou", e o único lugar onde ele cabe. */}
        {active && lens.decisions ? (
          <Badge tone="neutral">{tn('accessMap.lens.decisions', lens.decisions)}</Badge>
        ) : null}
      </div>

      {advanced ? (
        <div className={s.lensAdvanced} data-testid="lens-advanced">
          <span className={s.lensField}>
            <TextInput
              aria-label={t('accessMap.lens.claimName')}
              placeholder={t('accessMap.lens.claimNamePlaceholder')}
              value={claim}
              disabled={busy}
              onChange={(event) => setClaim(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addClaim();
                }
              }}
            />
          </span>
          <span className={s.lensEquals}>=</span>
          <span className={s.lensField}>
            <TextInput
              aria-label={t('accessMap.lens.claimValue')}
              placeholder={t('accessMap.lens.claimValuePlaceholder')}
              value={value}
              disabled={busy}
              onChange={(event) => setValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addClaim();
                }
              }}
            />
          </span>
          <Button
            variant="out"
            size="sm"
            icon="plus"
            data-testid="lens-add-claim"
            disabled={busy || !claim.trim() || !value.trim()}
            onClick={addClaim}
          >
            {t('accessMap.lens.add')}
          </Button>
          <button
            type="button"
            className={s.lensBack}
            data-testid="lens-back"
            disabled={busy}
            onClick={() => {
              setAdvanced(false);
              onChange(LENS_EMPTY);
            }}
          >
            {CLOSE_ADVANCED()}
          </button>
        </div>
      ) : null}

      {selection.claims.length > 0 ? (
        <div className={s.lensClaims} data-testid="lens-claims">
          {selection.claims.map((par: ClaimPair, index) => (
            <span key={`${par.claim}=${par.value}`} className={s.lensClaim}>
              <Chip>{`${par.claim} = ${par.value}`}</Chip>
              <IconButton
                icon="close"
                size="sm"
                label={t('accessMap.lens.removeClaim', { claim: par.claim, value: par.value })}
                disabled={busy}
                onClick={() => removeClaim(index)}
              />
            </span>
          ))}
        </div>
      ) : null}
    </section>
  );
}
